/**
 * Mein Tag – Einstiegsseite.
 *
 * Zweispaltig ab 1080 px: links die Arbeit, rechts das Team. Vorher lag alles
 * in einer 720 px schmalen Spalte, während zwei Drittel der Fläche leer
 * blieben und der Team-Überblick unter den Falz rutschte.
 *
 * Die große Zahl ist exakt die Länge der Liste darunter – keine gekürzte
 * Ansicht mit „3 weitere sind fällig" daneben. Was nicht dran ist, steht als
 * ein ruhiger Satz am Fuß der Karte.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconInfo, IconTelefon, IconMail } from '../komponenten/Icons';
import { tageSeit, whatsappNummer } from '../hilfen/format';
import { AnrufModal } from '../modals/AnrufModal';
import { MailAnsichtModal } from '../modals/MailAnsichtModal';
import type { Angebot, Erinnerung } from '../typen';
import './meintag.css';

/**
 * Ab wann eine Fälligkeit als dringend gilt.
 *
 * An einer Stelle und benannt, damit sie sich ändern lässt, ohne die
 * Darstellung zu durchsuchen. Sieben Tage sind eine Woche – wer länger nicht
 * angerufen hat, hat den Vorgang aus den Augen verloren.
 */
const DRINGEND_AB_TAGEN = 7;

type Zeitraum = 'woche' | 'monat';

/** Ein Vorgang, der heute Handarbeit braucht. */
interface Aufgabe {
  angebot: Angebot;
  /** Tage über der Fälligkeit. 0 = heute fällig, negativ kommt nicht vor. */
  tage: number;
  /** „Anruf ausstehend" oder „Wiedervorlage" */
  art: string;
  nummer: string;
}

export function MeinTag() {
  const { nutzer, modalOeffnen, darf } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();

  const [zeitraum, setZeitraum] = useState<Zeitraum>('woche');
  const [alleMails, setAlleMails] = useState(false);
  /** Vorgang, für den gerade eine Rückmeldung erfasst wird. */
  const [rueckmeldung, setRueckmeldung] = useState<Angebot | null>(null);
  /** Mail, die gerade angesehen wird – so, wie der Kunde sie bekommen hat. */
  const [mailAnsicht, setMailAnsicht] = useState<{ mail: Erinnerung; angebot: Angebot } | null>(null);

  const angeboteAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
    enabled: darf('monitor'),
  });

  const angebote = useMemo(() => angeboteAbfrage.data ?? [], [angeboteAbfrage.data]);
  const meinName = nutzer?.name ?? '';

  /**
   * „Meine" Vorgänge: die, die ich führe – Versender, ersatzweise Kalkulant.
   * Dieselbe Regel wie im Team-Überblick und im Cockpit.
   */
  const meine = useMemo(
    () => angebote.filter((a) => (a.versender || a.kalkulant) === meinName),
    [angebote, meinName],
  );

  /**
   * Was heute Handarbeit braucht, nach Überfälligkeit sortiert.
   *
   * Die Tage-Angabe ersetzt den fünffach wiederholten Badge „Anruf
   * ausstehend": Dass etwas ansteht, sagt schon die Liste – wie dringend,
   * sagte bisher nichts.
   */
  const aufgaben = useMemo<Aufgabe[]>(() => {
    const liste: Aufgabe[] = [];
    for (const a of meine) {
      if (a.wvFaellig && a.wiedervorlage) {
        liste.push({
          angebot: a,
          tage: Math.max(0, tageSeit(a.wiedervorlage, meta.heute)),
          art: 'Wiedervorlage',
          nummer: a.name,
        });
      } else if (a.nachfass.anrufFaellig) {
        liste.push({
          angebot: a,
          tage: Math.max(0, tageSeit(a.nachfass.anrufAb, meta.heute)),
          art: 'Anruf ausstehend',
          nummer: a.name,
        });
      }
    }
    return liste.sort((x, y) => y.tage - x.tage);
  }, [meine, meta.heute]);

  const dringend = aufgaben.filter((x) => x.tage >= DRINGEND_AB_TAGEN).length;

  /**
   * Vorgänge beim Kunden, die noch nicht dran sind.
   *
   * Als ein Satz am Fuß, nicht als zweite Zahl im Kopf: Vorher standen dort
   * drei Zahlen, die der Nutzer selbst zusammenrechnen musste.
   */
  const nochNichtDran = meine.filter((a) => a.offen).length - aufgaben.length;

  /** Protokoll der automatisch versendeten Mails – keine Aufgabe, eigene Karte. */
  const mails = useMemo(() => {
    const liste: { mail: Erinnerung; angebot: Angebot }[] = [];
    for (const a of meine) {
      for (const e of a.erinnerungen) {
        if (e.art !== 'auto') continue;
        const passt =
          zeitraum === 'monat'
            ? e.datum.slice(0, 7) === meta.heute.slice(0, 7)
            : tageSeit(e.datum, meta.heute) <= 7;
        if (passt) liste.push({ mail: e, angebot: a });
      }
    }
    return liste.sort((x, y) => (x.mail.datum + x.mail.zeit < y.mail.datum + y.mail.zeit ? 1 : -1));
  }, [meine, zeitraum, meta.heute]);

  const sichtbareMails = alleMails ? mails : mails.slice(0, 5);

  /**
   * Team-Überblick – für alle sichtbar, ohne Rechteprüfung.
   *
   * Jeder soll sehen, wie die Angebote der anderen stehen. Beschränkt bleiben
   * nur Auftragsübersicht und Finanzen, also die Bereiche mit Beträgen.
   *
   * Der eigene Eintrag entfällt: Er wäre dieselbe Aussage wie die Karte links.
   */
  const team = useMemo(() => {
    const offenAlle = angebote.filter((a) => a.offen);
    return meta.team
      .filter((p) => p.imVertrieb && p.name !== meinName)
      .map((p) => {
        const seine = offenAlle.filter((a) => (a.versender || a.kalkulant) === p.name);
        return {
          ...p,
          anzahl: seine.length,
          faellig: seine.filter((a) => a.nachfass.anrufFaellig || a.wvFaellig).length,
        };
      })
      .filter((t) => t.anzahl > 0)
      .sort((x, y) => y.faellig - x.faellig || y.anzahl - x.anzahl);
  }, [angebote, meta.team, meinName]);

  /** Erste erreichbare Nummer des Kunden – der Prozess ist anrufgetrieben. */
  function nummerVon(a: Angebot): string {
    return a.kunde.telefon || a.kunde.mobil || a.kunde.mobilGeschaeftlich || '';
  }

  return (
    <div className="tag">
      <div className="tag-kopf">
        <div>
          <div className="tag-kicker">{meta.heuteText}</div>
          <h1 className="tag-gruss">Willkommen, {nutzer?.name}</h1>
        </div>
        {darf('monitor') && (
          <button type="button" className="tag-ausgang" onClick={() => navigate('/cockpit')}>
            Alle Vorgänge im Vertriebscockpit →
          </button>
        )}
      </div>

      {!darf('monitor') ? (
        <div className="eh-karte">
          Für dich ist der Vertriebsbereich nicht freigeschaltet. Die Freigabe erteilt die
          Administration im Admin Center.
        </div>
      ) : (
        <div className="tag-raster">
          <div className="tag-spalte">
            {/* --- Auf deinem Tisch --- */}
            <div className="tag-tisch">
              <div className="tag-tisch-kopf">
                <div className="tag-label">Auf deinem Tisch</div>
                {dringend > 0 && (
                  <span className="tag-eskalation">
                    <span className="tag-eskalation-punkt" />
                    {dringend} länger als {DRINGEND_AB_TAGEN} Tage
                  </span>
                )}
              </div>

              <div className="tag-zahl-zeile">
                <span className={`tag-zahl ${aufgaben.length ? '' : 'ruhig'}`}>
                  {aufgaben.length}
                </span>
                <span className="tag-zahl-text">
                  {aufgaben.length === 1
                    ? 'Vorgang braucht heute deine Handlung'
                    : 'Vorgänge brauchen heute deine Handlung'}
                </span>
              </div>

              <div className="tag-liste">
                {aufgaben.map((x) => (
                  <div
                    key={x.angebot.id}
                    className="tag-zeile"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/angebot/${x.angebot.id}`, { state: { herkunft: 'meintag' } })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        navigate(`/angebot/${x.angebot.id}`, { state: { herkunft: 'meintag' } });
                      }
                    }}
                  >
                    <div className="tag-zeile-links">
                      <div className={`tag-nummer ${x.tage >= DRINGEND_AB_TAGEN ? 'dringend' : ''}`}>
                        {x.nummer}
                      </div>
                      <div className="tag-kunde">
                        {x.angebot.kunde.ort} · {x.angebot.kunde.name}
                      </div>
                    </div>

                    <div className="tag-zeile-mitte">
                      <div className={`tag-tage ${stufe(x.tage)}`}>{tageText(x.tage)}</div>
                      <div className="tag-art">{x.art}</div>
                    </div>

                    {/*
                      Handeln ohne Umweg. Der Prozess ist anrufgetrieben – wer
                      für jeden Anruf erst das Angebot öffnen muss, arbeitet die
                      Liste nicht ab. Die Knöpfe stoppen das Klick-Ereignis,
                      sonst öffnete sich zusätzlich die Detailmaske.
                    */}
                    <div className="tag-aktionen">
                      <a
                        className={`tag-aktion ${nummerVon(x.angebot) ? '' : 'ruht'}`}
                        href={nummerVon(x.angebot) ? `tel:${whatsappNummer(nummerVon(x.angebot))}` : undefined}
                        title={
                          nummerVon(x.angebot)
                            ? `${nummerVon(x.angebot)} anrufen`
                            : 'Keine Telefonnummer hinterlegt'
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconTelefon groesse={15} />
                      </a>
                      <button
                        type="button"
                        className="tag-aktion"
                        title="Rückmeldung erfassen"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRueckmeldung(x.angebot);
                        }}
                      >
                        <IconSprechblase groesse={15} />
                      </button>
                    </div>
                  </div>
                ))}

                {aufgaben.length === 0 && (
                  <div className="tag-leer">
                    Heute ist nichts fällig. Vorgänge erscheinen hier automatisch, sobald ein Anruf
                    oder eine Wiedervorlage ansteht.
                  </div>
                )}
              </div>

              {nochNichtDran > 0 && (
                <div className="tag-fuss-satz">
                  Weitere {nochNichtDran} {nochNichtDran === 1 ? 'Angebot liegt' : 'Angebote liegen'}{' '}
                  beim Kunden, {nochNichtDran === 1 ? 'ist' : 'sind'} aber noch nicht dran –{' '}
                  {nochNichtDran === 1 ? 'es erscheint' : 'sie erscheinen'} hier automatisch, sobald
                  eine Handlung fällig wird.
                </div>
              )}
            </div>

            {/* --- Mail-Protokoll: eigene Karte, keine Aufgabe --- */}
            <div className="tag-karte">
              <div className="tag-tisch-kopf">
                <div className="tag-label">
                  {zeitraum === 'monat'
                    ? 'Diesen Monat automatisch versendet'
                    : 'Diese Woche automatisch versendet'}
                </div>
                {/* Die Zeitraum-Chips erscheinen nur, wenn es etwas zu filtern
                    gibt – über einer leeren Liste steuerten sie nichts. */}
                {mails.length > 0 && (
                  <div className="tag-chips">
                    <button
                      type="button"
                      className={`tag-chip ${zeitraum === 'woche' ? 'aktiv' : ''}`}
                      onClick={() => setZeitraum('woche')}
                    >
                      Diese Woche
                    </button>
                    <button
                      type="button"
                      className={`tag-chip ${zeitraum === 'monat' ? 'aktiv' : ''}`}
                      onClick={() => setZeitraum('monat')}
                    >
                      Dieser Monat
                    </button>
                  </div>
                )}
              </div>

              <div className="tag-mails">
                {sichtbareMails.map(({ mail, angebot }) => (
                  <button
                    type="button"
                    className="tag-mail"
                    key={`${angebot.id}-${mail.id}`}
                    onClick={() => setMailAnsicht({ mail, angebot })}
                    title="Zeigt die Mail so, wie der Kunde sie bekommen hat"
                  >
                    <span className="tag-mail-links">
                      <span className="tag-mail-name">{angebot.name}</span>
                      <span className="tag-mail-art">
                        {mail.stufe}. Erinnerung
                        {mail.trockenlauf ? ' · Testbetrieb, nicht versendet' : ''}
                      </span>
                    </span>
                    <span className="tag-mail-zeit">
                      {mail.wochentag}, {mail.datumText} · {mail.zeit || '—'}
                    </span>
                    <span className="tag-mail-auge">
                      <IconMail groesse={14} />
                    </span>
                  </button>
                ))}

                {mails.length === 0 && (
                  <div className="tag-leer">
                    {zeitraum === 'monat'
                      ? 'In diesem Monat ging keine automatische Erinnerung raus.'
                      : 'Diese Woche ging keine automatische Erinnerung raus.'}
                  </div>
                )}
              </div>

              {mails.length > sichtbareMails.length && (
                <button type="button" className="tag-mehr" onClick={() => setAlleMails(true)}>
                  Alle {mails.length} anzeigen
                </button>
              )}
            </div>
          </div>

          {/* --- Rechte Spalte --- */}
          <div className="tag-seitenspalte">
            <div className="tag-karte">
              <div className="tag-label">Team-Überblick</div>
              <div className="tag-team">
                {team.map((p) => (
                  <div className="tag-person" key={p.name}>
                    <span className="tag-avatar">{p.initialen}</span>
                    <span className="tag-person-text">
                      <span className="tag-person-name">{p.name}</span>
                      <span className="tag-person-meta">
                        {p.anzahl} {p.anzahl === 1 ? 'Angebot' : 'Angebote'} beim Kunden
                      </span>
                    </span>
                    <span className={`tag-person-faellig ${p.faellig ? 'offen' : ''}`}>
                      {p.faellig === 0
                        ? 'nichts fällig'
                        : p.faellig === 1
                          ? '1 Handlung fällig'
                          : `${p.faellig} Handlungen fällig`}
                    </span>
                  </div>
                ))}

                {team.length === 0 && (
                  <div className="tag-leer">Bei den anderen liegt gerade kein Angebot.</div>
                )}
              </div>

              <div className="tag-team-hinweis">
                Deine eigenen Vorgänge stehen links unter „Auf deinem Tisch" – hier stehen nur die
                anderen.
              </div>
            </div>

            <div className="tag-fuss">
              <span>
                Kora {meta.version}
                {meta.trockenlauf ? ' · Testbetrieb, Kundenmails werden nicht versendet' : ''}
              </span>
              <button
                type="button"
                className="tag-info"
                title="Was ist neu?"
                onClick={() => modalOeffnen({ art: 'changelog', alle: true })}
              >
                <IconInfo />
              </button>
            </div>
          </div>
        </div>
      )}

      {rueckmeldung && (
        <AnrufModal
          angebot={rueckmeldung}
          onSchliessen={() => setRueckmeldung(null)}
          onFertig={() => angeboteAbfrage.refetch()}
        />
      )}

      {mailAnsicht && (
        <MailAnsichtModal
          mail={mailAnsicht.mail}
          angebot={mailAnsicht.angebot}
          onSchliessen={() => setMailAnsicht(null)}
        />
      )}
    </div>
  );
}

/**
 * Farbstufe der Fälligkeit.
 *
 * Farbe nur als Eskalation, nicht als Dekoration: Wäre jede Zeile farbig,
 * bedeutete die Farbe nichts mehr.
 */
function stufe(tage: number): string {
  if (tage >= DRINGEND_AB_TAGEN) return 'rot';
  if (tage >= 1) return 'gelb';
  return 'neutral';
}

function tageText(tage: number): string {
  if (tage <= 0) return 'heute fällig';
  return `${tage} ${tage === 1 ? 'Tag' : 'Tage'} überfällig`;
}

/** Sprechblase – steht für „Rückmeldung erfassen". */
function IconSprechblase({ groesse = 15 }: { groesse?: number }) {
  return (
    <svg
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path d="M20 12a7.5 7.5 0 0 1-7.5 7.5H8l-4 3v-4.4A7.5 7.5 0 0 1 12.5 4.5 7.5 7.5 0 0 1 20 12Z" />
    </svg>
  );
}